import { useEffect, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';

import {
  VerifyAndSubmitServiceRequestChargeMutationMutation,
  VerifyAndSubmitServiceRequestChargeMutationMutationVariables,
} from 'graphql-query-contracts';
import { verifyAndSubmitServiceRequestChargeMutation } from 'graphql-queries';

import { ChargeRequestFromDraft } from './createChargeRequestFromDraft';
import {
  canRetry,
  classifyFailure,
  VerifyAndSubmitFailure,
} from './verifyAndSubmitFailure';

type MutationVariables = {
  input: Omit<
    VerifyAndSubmitServiceRequestChargeMutationMutationVariables['input'],
    'createChargeRequest'
  > & { createChargeRequest: ChargeRequestFromDraft };
};

type Attempt = { chargeId?: string; previous?: VerifyAndSubmitFailure };

type SentSubmission<TContext> = {
  request: ChargeRequestFromDraft;
  context: TContext;
};

export type CompletedSubmission<TContext> = SentSubmission<TContext> & {
  payload: NonNullable<
    VerifyAndSubmitServiceRequestChargeMutationMutation['verifyAndSubmitServiceRequestCharge']
  >;
};

// The context travels with the request that was actually sent, so a retry can
// rebuild the result from what the server received rather than from current props.
export function useVerifyAndSubmitCharge<TContext>(serviceRequestId: string) {
  const [verifyAndSubmitCharge, { loading: isSubmitting }] = useMutation<
    VerifyAndSubmitServiceRequestChargeMutationMutation,
    MutationVariables
  >(verifyAndSubmitServiceRequestChargeMutation);
  const [failure, setFailure] = useState<VerifyAndSubmitFailure>();
  // Refs, not state: a guard or retry fired right after a failure must not read stale state.
  const isInFlight = useRef(false);
  const currentServiceRequestId = useRef(serviceRequestId);
  const lastSent = useRef<SentSubmission<TContext> | undefined>(undefined);
  const lastFailure = useRef<VerifyAndSubmitFailure | undefined>(undefined);

  useEffect(() => {
    currentServiceRequestId.current = serviceRequestId;
    isInFlight.current = false;
    lastSent.current = undefined;
    lastFailure.current = undefined;
    setFailure(undefined);
  }, [serviceRequestId]);

  const send = async (
    sent: SentSubmission<TContext>,
    { chargeId, previous }: Attempt
  ) => {
    if (isInFlight.current) {
      return undefined;
    }

    isInFlight.current = true;
    lastSent.current = sent;
    const isStale = () => serviceRequestId !== currentServiceRequestId.current;

    try {
      const { data } = await verifyAndSubmitCharge({
        variables: {
          input: {
            serviceRequestId,
            createChargeRequest: sent.request,
            ...(chargeId && { chargeId }),
          },
        },
      });

      if (isStale()) {
        return undefined;
      }

      lastFailure.current = undefined;
      setFailure(undefined);

      const payload = data?.verifyAndSubmitServiceRequestCharge;

      return payload && { payload, ...sent };
    } catch (error) {
      if (isStale()) {
        return undefined;
      }

      lastFailure.current = classifyFailure(error, previous);
      setFailure(lastFailure.current);

      return undefined;
    } finally {
      if (!isStale()) {
        isInFlight.current = false;
      }
    }
  };

  const submit = async (
    createChargeRequest: ChargeRequestFromDraft,
    context: TContext
  ) => {
    // A failure that holds a charge id means the charge already exists.
    if (lastFailure.current?.chargeId) {
      return undefined;
    }

    return send({ request: createChargeRequest, context }, {});
  };

  const retry = async () => {
    const previous = lastFailure.current;

    if (!previous || !lastSent.current || !canRetry(previous)) {
      return undefined;
    }

    return send(lastSent.current, { chargeId: previous.chargeId, previous });
  };

  return { submit, retry, isSubmitting, failure };
}
